import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { SidebarProvider } from "@/components/ui/sidebar";
import GuardianHeader from "./GuardianHeader";
import GuardianSidebar from "./GuardianSidebar";
import { Outlet, useNavigate } from "react-router-dom";
import { auth, db, functions } from "@/firebase/config";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
  doc,
} from "firebase/firestore";
import { useToast } from "@/hooks/use-toast";


import { calculateFeeBalances, getApplicableTermFees, Term, getTermKeyFromDate } from "@/utils/feeCalculations";
import { Loader2, DollarSign, PlayCircle, Phone, Coins, Building2, Receipt } from "lucide-react";
import GuardianTransactions from "./GuardianTransactions";
import { FeeStructureContent } from "./FeeStructure";
import { Button } from "@/components/ui/button";
import PaymentSimulator from "@/components/finance/PaymentSimulator";





interface Child {
  studentUid: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
}

// Transaction interface removed; transactions handled in GuardianTransactions component

const GuardianFinance = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.users?.currentUser || {});
  const { selectedStudent } = user;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Simulator State
  const [simState, setSimState] = useState<{
    isOpen: boolean;
    studentId: string;
    studentName: string;

    feeTotals?: { term1: number; term2: number; term3: number; annual: number };
    openingBalance?: number;
  }>({
    isOpen: false,
    studentId: "",
    studentName: "",
  });

  const [amount, setAmount] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [accountReference, setAccountReference] = useState<string>("");
  const [payLoading, setPayLoading] = useState<boolean>(false);
  const [payResult, setPayResult] = useState<any>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "payments" | "feeStructure">("overview");
  const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<"idle" | "processing" | "completed" | "failed">("idle");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<{
    amount: number;
    receipt?: string;
    message: string;
  } | null>(null);
  const [totalFeesPaid, setTotalFeesPaid] = useState<number>(0);
  const [loadingTotalFees, setLoadingTotalFees] = useState(false);
  const [totalFeesRequired, setTotalFeesRequired] = useState<number>(0);
  const [loadingFeesRequired, setLoadingFeesRequired] = useState(false);
  const [balance, setBalance] = useState<number>(0);
  // Per-child aggregates
  const [childPayments, setChildPayments] = useState<Record<string, number>>({}); // key: registrationNumber
  const [childFeesRequiredMap, setChildFeesRequiredMap] = useState<Record<string, { 
    totals: { term1: number; term2: number; term3: number; annual: number }, 
    grossTotals?: { term1: number; term2: number; term3: number; annual: number },
    type: string, 
    governmentApplied?: { term1: number; term2: number; term3: number; annual: number }, 
    isGovernmentSponsored?: boolean, 
    governmentSponsorshipAmount?: number,
    admissionDateMissing?: boolean,
    admissionDate?: string,
    errorMessage?: string,
    nextClassTerm1Fee?: number,
    nextClassName?: string,
    className?: string,
    termIds?: { term1: number; term2: number; term3: number },
    openingBalance?: number
  }>>({});
  const [selectedTerm, setSelectedTerm] = useState<'term1' | 'term2' | 'term3'>('term1');
  const [childPaymentsByTerm, setChildPaymentsByTerm] = useState<Record<string, { term1: number; term2: number; term3: number }>>({});
  const [termRanges, setTermRanges] = useState<null | {
    term1: { start: Date; end: Date };
    term2: { start: Date; end: Date };
    term3: { start: Date; end: Date };
  }>(null);

  // Term IDs from deployed fee structure (for payment integration)
  const [termIdsByClass, setTermIdsByClass] = useState<Record<string, { term1: number; term2: number; term3: number }>>({});
  const [terms, setTerms] = useState<Term[]>([]); // Added for dynamic fee calculation

  // Fetch enabled payment methods
  const [paymentProviders, setPaymentProviders] = useState<string[]>(['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank']); // Default fallback
  const [showLess, setShowLess] = useState(false); // Toggle for simplified view

  useEffect(() => {
    if (!schoolID) return;
    const fetchPaymentMethods = async () => {
      try {
        // Try to fetch configured methods
        const methodsRef = collection(db, `schools/${schoolID}/systemConfig/paymentMethods/list`);
        const snap = await getDocs(methodsRef);
        
        if (!snap.empty) {
          const params: string[] = [];
          snap.forEach(doc => {
            if (doc.data().enabled) {
              params.push(doc.id); // Assuming doc ID is the provider key e.g. 'mpesa'
            }
          });
          // Also check single config doc as alternative structure
          if (params.length === 0) {
             const configDoc = await getDoc(doc(db, `schools/${schoolID}/systemConfig/paymentMethods`));
             if (configDoc.exists()) {
               const data = configDoc.data();
               const methods = data.methods || []; // Expecting array of objects or strings
               methods.forEach((m: any) => {
                 if (typeof m === 'string') params.push(m);
                 else if (m.enabled && m.id) params.push(m.id);
               });
             }
          }

          if (params.length > 0) {
             if (!params.includes('bank')) params.push('bank');
             setPaymentProviders(Array.from(new Set(params)));
             console.log("[GuardianFinance] Loaded dynamic payment providers:", params);
          }
        }
      } catch (e) {
        console.warn("Failed to load payment methods, using defaults", e);
      }
    };
    fetchPaymentMethods();
  }, [schoolID]);

  const addDebugLog = (message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    const logEntry = data 
      ? `[${timestamp}] ${message}\n${JSON.stringify(data, null, 2)}`
      : `[${timestamp}] ${message}`;
    setDebugLogs(prev => [...prev, logEntry]);
    console.log(message, data || '');
  };

  // Transactions moved to dedicated page

  // ===== Term dates helpers and loader =====
  const parseMMDD = (mmdd: string, year: number) => {
    const [mStr, dStr] = (mmdd || "").split("-");
    const m = Math.max(1, Math.min(12, Number(mStr)));
    const d = Math.max(1, Math.min(31, Number(dStr)));
    return new Date(year, m - 1, d, 0, 0, 0, 0);
  };

  const buildTermRanges = (t: { term1Start: string; term1End: string; term2Start: string; term2End: string; term3Start: string; term3End: string; }) => {
    const now = new Date();
    const y = now.getFullYear();
    let t1s = parseMMDD(t.term1Start, y);
    let t1e = parseMMDD(t.term1End, y);
    let t2s = parseMMDD(t.term2Start, y);
    let t2e = parseMMDD(t.term2End, y);
    let t3s = parseMMDD(t.term3Start, y);
    let t3e = parseMMDD(t.term3End, y);
    if (t2s < t1s) { t2s = parseMMDD(t.term2Start, y + 1); t2e = parseMMDD(t.term2End, y + 1); }
    if (t3s < t2s) { t3s = parseMMDD(t.term3Start, t2s.getFullYear()); t3e = parseMMDD(t.term3End, t2s.getFullYear()); if (t3e < t3s) t3e = parseMMDD(t.term3End, t3s.getFullYear()); }
    return { term1: { start: t1s, end: t1e }, term2: { start: t2s, end: t2e }, term3: { start: t3s, end: t3e } };
  };

  const dateInRange = (d: Date, r: { start: Date; end: Date }) => d >= r.start && d <= r.end;
  const classifyDateToTerm = (d: Date, ranges: NonNullable<typeof termRanges>): 'term1'|'term2'|'term3'|null => {
    if (dateInRange(d, ranges.term1)) return 'term1';
    if (dateInRange(d, ranges.term2)) return 'term2';
    if (dateInRange(d, ranges.term3)) return 'term3';
    return null;
  };

  /**
   * Determines the current term based on actual term dates.
   * Logic:
   * 1. If current date is within a term's date range (opening to closing), return that term
   * 2. If current date is between terms (holiday/break), return the next upcoming term
   * 3. If current date is before all terms, return term1
   * 4. If current date is after all terms, return term1 (next year cycle)
   */
  const getCurrentTermFromRanges = (now: Date, ranges: NonNullable<typeof termRanges>): 'term1'|'term2'|'term3' => {
    // Check if we're currently within any term's date range
    if (dateInRange(now, ranges.term1)) return 'term1';
    if (dateInRange(now, ranges.term2)) return 'term2';
    if (dateInRange(now, ranges.term3)) return 'term3';
    
    // We're in a break/holiday period - find the next upcoming term
    // or the most recently ended term if we're past all terms
    
    // Before term 1 starts
    if (now < ranges.term1.start) return 'term1';
    
    // Between term 1 end and term 2 start - show term 1 (just finished) or term 2 (upcoming)
    if (now > ranges.term1.end && now < ranges.term2.start) return 'term1';
    
    // Between term 2 end and term 3 start
    if (now > ranges.term2.end && now < ranges.term3.start) return 'term2';
    
    // After term 3 ends - next cycle starts with term 1
    if (now > ranges.term3.end) return 'term1';
    
    // Default fallback
    return 'term1';
  };

  // Load term dates: prioritize school-specific terms, fallback to global
  useEffect(() => {
    const loadSchoolTerms = async () => {
      if (!schoolID) return false;
      
      try {
        // Try to load from school-specific terms collection first
        const termsSnap = await getDocs(collection(db, `schools/${schoolID}/terms`));
        if (!termsSnap.empty) {
          const termsData: Record<string, { start: Date; end: Date }> = {};
          
          termsSnap.docs.forEach((doc) => {
            const data = doc.data();
            const termNumber = data.number;
            
            // Parse dates - they are stored as yyyy-MM-dd strings
            const parseDate = (dateStr: string) => {
              if (!dateStr) return null;
              const d = new Date(dateStr);
              return isNaN(d.getTime()) ? null : d;
            };
            
            const openingDate = parseDate(data.openingDate);
            const closingDate = parseDate(data.closingDate);
            
            if (termNumber && openingDate && closingDate) {
              termsData[`term${termNumber}`] = { start: openingDate, end: closingDate };
            }
          });
          
          if (termsData.term1 && termsData.term2 && termsData.term3) {
            setTermRanges({
              term1: termsData.term1,
              term2: termsData.term2,
              term3: termsData.term3,
            });
            // Auto-select current term
            const ranges = {
               term1: termsData.term1,
               term2: termsData.term2,
               term3: termsData.term3,
            };
            const now = new Date();
            const current = getCurrentTermFromRanges(now, ranges);
            setSelectedTerm(current);
            console.log("[GuardianFinance] Term detection:", {
              currentDate: now.toISOString().split('T')[0],
              term1: { start: termsData.term1.start.toISOString().split('T')[0], end: termsData.term1.end.toISOString().split('T')[0] },
              term2: { start: termsData.term2.start.toISOString().split('T')[0], end: termsData.term2.end.toISOString().split('T')[0] },
              term3: { start: termsData.term3.start.toISOString().split('T')[0], end: termsData.term3.end.toISOString().split('T')[0] },
              detectedCurrentTerm: current
            });
            console.log("[GuardianFinance] Loaded school-specific term dates:", termsData);
            return true;
          }
        }
      } catch (e) {
        console.warn("[GuardianFinance] Failed to load school terms:", e);
      }
      return false;
    };
    
    const loadGlobalTerms = async () => {
      try {
        const configRef = doc(db, "systemConfig/global");
        const snap = await getDoc(configRef);
        if (snap.exists()) {
          const data = snap.data() as { termDates?: any };
          if (data?.termDates) {
            setTermRanges(buildTermRanges(data.termDates));
            // Auto-select current term
            const ranges = buildTermRanges(data.termDates);
            const current = getCurrentTermFromRanges(new Date(), ranges);
            setSelectedTerm(current);
            console.log("[GuardianFinance] Loaded global term dates (fallback):", data.termDates);
          }
        }
      } catch (e) {
        console.warn("[GuardianFinance] Failed to load global term dates:", e);
      }
    };
    
    const loadTerms = async () => {
      // First try school-specific terms
      const schoolTermsLoaded = await loadSchoolTerms();
      
      // If school terms don't exist, fallback to global
      if (!schoolTermsLoaded) {
        await loadGlobalTerms();
      }
    };
    
    loadTerms();
  }, [schoolID]);

  // Fetch outstanding balances for children (historical debt)
  const [childOutstandingBalances, setChildOutstandingBalances] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!schoolID || children.length === 0) return;

    const fetchOutstandingBalances = async () => {
      const balances: Record<string, number> = {};
      
      for (const child of children) {
        if (!child.studentUid) continue;
        try {
          const studentDoc = await getDoc(doc(db, `schools/${schoolID}/students/${child.studentUid}`));
          if (studentDoc.exists()) {
             const data = studentDoc.data();
             balances[child.registrationNumber] = data.outstandingBalance || 0;
          }
        } catch (e) {
          console.error("Error fetching outstanding balance for", child.firstName, e);
        }
      }
      setChildOutstandingBalances(balances);
    };

    fetchOutstandingBalances();
  }, [schoolID, children]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        toast({
          title: "Authentication Required",
          description: "Please log in to access finance.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/guardian-login");
        setIsLoading(false);
        return;
      }

      // Pre-emptively set access if Redux already knows the role
      if (user.selectedAffiliation?.role === 'guardian') {
        setHasAccess(true);
      }

      try {
        // Log basic session info for debugging
        addDebugLog(`Session Initialized for UID: ${firebaseUser.uid}`);
        
        // 1. If Redux has a selected student with full details, use it.
        if (selectedStudent && selectedStudent.firstName && selectedStudent.lastName) {
          console.log("GuardianFinance: Using selected student from Redux:", selectedStudent);
          addDebugLog(`Selected Student from Redux: ${selectedStudent.firstName} ${selectedStudent.lastName} (${selectedStudent.registrationNumber})`);
          addDebugLog(`Student SchoolID: ${selectedStudent.schoolID}`);
          addDebugLog(`User Subscription Status: ${user.subscriptionStatus || 'unknown'}`);
          
          setChildren([{
            studentUid: selectedStudent.studentUid,
            firstName: selectedStudent.firstName,
            lastName: selectedStudent.lastName,
            registrationNumber: selectedStudent.registrationNumber
          }]);
          setSchoolID(selectedStudent.schoolID || null);
          setAccountReference(`Fee-${selectedStudent.registrationNumber}`);
          setHasAccess(true);
          setIsLoading(false);
          return;
        }

        // 2. Verify claims if not already certain from Redux
        const token = await getIdTokenResult(firebaseUser);
        const claims = token.claims as any;
        addDebugLog("Raw Token Claims:", claims);
        addDebugLog(`Active flat schoolID claim: "${claims.schoolID || ''}"`);
        
        const isGuardian = claims.role === 'guardian' || 
                          claims.affiliations?.some((a: any) => a.role === 'guardian');
        
        if (isGuardian) {
          setHasAccess(true);
        } else {
          addDebugLog("CRITICAL: User is logged into Guardian portal but does not have 'guardian' role in claims.");
          setHasAccess(false);
          setIsLoading(false);
          return;
        }

        // 3. If we are a guardian but still waiting for student details from DataPrefetcher
        console.log("GuardianFinance: Waiting for full selectedStudent details...");
        addDebugLog("Waiting for detailed student data from Redux prefetcher...");
        setIsLoading(true);
        return;

      } catch (error: any) {
        addDebugLog(`Guard Error: ${error.message}`, error);
        console.error("Guardian access validation error:", error);
        const isNetworkError = error?.code === 'auth/network-request-failed' ||
                              error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                              error?.message?.includes('network-request-failed') ||
                              error?.message?.includes('Failed to fetch');
        
        const isAuthError = !isNetworkError && (
                           error?.code === 'auth/invalid-user-token' ||
                           error?.code === 'auth/user-token-expired' ||
                           error?.code === 'auth/user-disabled' ||
                           error?.message?.includes('permission-denied') ||
                           error?.message?.includes('unauthenticated'));
        
        if (isAuthError) {
          toast({
            title: "Authentication Error",
            description: "Your session has expired. Please log in again.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/guardian-login");
        } else {
          toast({
            title: "Connection Error",
            description: "Failed to load data. Please check your internet connection and try again.",
            variant: "destructive",
            duration: 5000,
          });
        }
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, toast, selectedStudent, user.selectedAffiliation?.role, dispatch]);


  useEffect(() => {
    if (!schoolID || children.length === 0) {
      setTotalFeesPaid(0);
      return;
    }

    setLoadingTotalFees(true);

    const fetchTotalFees = async () => {
      try {
        let total = 0;
        const perChild: Record<string, number> = {};
        const perChildTerm: Record<string, { term1: number; term2: number; term3: number }> = {};
        
        // Use dynamically fetched providers
        const providers = paymentProviders;
        console.log("Fetching transactions for providers:", providers);
        
        for (const child of children) {
          for (const provider of providers) {
            try {
              const txQuery = query(
                collection(db, `schools/${schoolID}/transactions/${provider}/records`),
                where("accountReference", "==", `Fee-${child.registrationNumber}`),
                where("status", "==", "completed")
              );

              const txSnapshot = await getDocs(txQuery);
              txSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.amount) {
                  const amt = Number(data.amount);
                  total += amt;
                  perChild[child.registrationNumber] = (perChild[child.registrationNumber] || 0) + amt;

                  if (!perChildTerm[child.registrationNumber]) {
                    perChildTerm[child.registrationNumber] = { term1: 0, term2: 0, term3: 0 };
                  }

                  // PRIORITY 1: Use locked term info if available (new payments have this)
                  const lockedTermKey = data.termKey as string | undefined;
                  const lockedTermNumber = data.paymentForTerm as 1 | 2 | 3 | undefined;
                  
                  if (lockedTermKey && (lockedTermKey.startsWith('term1') || lockedTermKey.startsWith('term2') || lockedTermKey.startsWith('term3'))) {
                    // Extract term number from termKey (e.g., "term1_2025" -> "term1")
                    const termPart = lockedTermKey.split('_')[0] as 'term1' | 'term2' | 'term3';
                    perChildTerm[child.registrationNumber][termPart] += amt;
                  } else if (lockedTermNumber && [1, 2, 3].includes(lockedTermNumber)) {
                    // Use paymentForTerm directly
                    const termKey = `term${lockedTermNumber}` as 'term1' | 'term2' | 'term3';
                    perChildTerm[child.registrationNumber][termKey] += amt;
                  } else {
                    // PRIORITY 2: Fallback to date-based classification for older payments
                    let txDate: Date | null = null;
                    const maybeDateFields = [
                      data?.mpesaDetails?.transactionDate,
                      data?.transactionDate,
                      data?.timestamp,
                      data?.createdAt,
                      data?.completedAt,
                      data?.lastUpdate,
                    ];
                    for (const f of maybeDateFields) {
                      if (!f) continue;
                      if (typeof f === 'string') {
                        if (/^\d{14}$/.test(f)) {
                          const Y = Number(f.slice(0,4));
                          const M = Number(f.slice(4,6)) - 1;
                          const D = Number(f.slice(6,8));
                          const h = Number(f.slice(8,10));
                          const m = Number(f.slice(10,12));
                          const s = Number(f.slice(12,14));
                          txDate = new Date(Y, M, D, h, m, s);
                          break;
                        }
                        const d = new Date(f);
                        if (!isNaN(d.getTime())) { txDate = d; break; }
                      } else if (typeof f?.toDate === 'function') {
                        try { txDate = f.toDate(); break; } catch {}
                      }
                    }
                    if (!txDate) txDate = new Date();

                    const tKey = termRanges ? classifyDateToTerm(txDate, termRanges) : null;
                    if (tKey) {
                      perChildTerm[child.registrationNumber][tKey] += amt;
                    } else {
                      const mon = txDate.getMonth();
                      const fallback = mon <= 3 ? 'term1' : mon <= 7 ? 'term2' : 'term3';
                      perChildTerm[child.registrationNumber][fallback] += amt;
                    }
                  }
                }
              });
            } catch (providerError) {
              console.error(`Error fetching ${provider} transactions:`, providerError);
            }
          }
        }

        setTotalFeesPaid(total);
        setChildPayments(perChild);
        setChildPaymentsByTerm(perChildTerm);
      } catch (error) {
        console.error("Error fetching total fees:", error);
        toast({
          title: "Error",
          description: "Failed to calculate total fees paid",
          variant: "destructive",
          duration: 3000,
        });
      } finally {
        setLoadingTotalFees(false);
      }
    };

    fetchTotalFees();

    // Set up real-time listener for transactions updates
    // We listen to the first provider in the list as a trigger for now, usually mpesa
    const unsubscribe = onSnapshot(
      collection(db, `schools/${schoolID}/transactions/${paymentProviders[0] || 'mpesa'}/records`),
      () => {
        fetchTotalFees();
      }
    );

    return () => unsubscribe();
  }, [schoolID, children, toast, termRanges, paymentProviders]);

  // Fetch total fees required based on deployed fee structure
  useEffect(() => {
    if (!schoolID || children.length === 0) {
      setTotalFeesRequired(0);
      return;
    }

    setLoadingFeesRequired(true);

    const fetchTotalFeesRequired = async () => {
      try {
        let totalRequired = 0;
        const perChildFees: Record<string, { 
          totals: { term1: number; term2: number; term3: number; annual: number }, 
          grossTotals?: { term1: number; term2: number; term3: number; annual: number },
          type: string, 
          governmentApplied?: { term1: number; term2: number; term3: number; annual: number }, 
          isGovernmentSponsored?: boolean, 
          governmentSponsorshipAmount?: number,
          admissionDateMissing?: boolean,
          admissionDate?: string,
          errorMessage?: string,
          nextClassTerm1Fee?: number,
          nextClassName?: string,
          className?: string,
          termIds?: { term1: number; term2: number; term3: number },
          openingBalance?: number
        }> = {};

        // Get deployed fee structure AND terms
        const feeStructureRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
        const termsRef = collection(db, `schools/${schoolID}/terms`);
        
        const [feeStructureSnap, termsSnap] = await Promise.all([
          getDoc(feeStructureRef),
          getDocs(termsRef)
        ]);

        if (!feeStructureSnap.exists()) {
          console.log("No deployed fee structure found");
          setTotalFeesRequired(0);
          setLoadingFeesRequired(false);
          return;
        }

        const feeStructure = feeStructureSnap.data();
        
        const termsList = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Term));
        setTerms(termsList); // Update state with fetched terms
        const levelFees = feeStructure.levelFees || [];

        // Extract and store termIdsByClass for payment integration
        if (feeStructure.termIdsByClass) {
          setTermIdsByClass(feeStructure.termIdsByClass);
          console.log("[GuardianFinance] Loaded termIdsByClass from deployed fee structure:", feeStructure.termIdsByClass);
        }

        // Calculate fees for each child
        for (const child of children) {
          // Get student details to find their class/level
          const studentQuery = query(
            collection(db, `schools/${schoolID}/students`),
            where("studentUid", "==", child.studentUid)
          );

          const studentSnap = await getDocs(studentQuery);
          
          if (!studentSnap.empty) {
            const studentData = studentSnap.docs[0].data();
            const studentClass = studentData.className || studentData.class || "";
            const studentGrade = studentData.admissionGrade || studentData.grade || "";
            const studentType = studentData.studentType || ""; // "Boarding" or "Day School"
            
            // Debug: Log all date-related fields in the student data
            console.log(`[GuardianFinance] Student ${child.firstName} data fields:`, {
              hasAdmissionDate: !!studentData.admissionDate,
              hasCreatedAt: !!studentData.createdAt,
              hasEnrollmentDate: !!studentData.enrollmentDate,
              hasAcademicYear: !!studentData.academicYear,
              hasAdmissionTerm: !!studentData.admissionTerm,
              isOngoingStudent: !!studentData.ongoing,
              outstandingBalance: studentData.outstandingBalance || 0,
              admissionDate: studentData.admissionDate,
              createdAt: studentData.createdAt,
              enrollmentDate: studentData.enrollmentDate,
              academicYear: studentData.academicYear,
              admissionTerm: studentData.admissionTerm,
              allKeys: Object.keys(studentData),
            });
            
            // ONLY use the admissionDate field - no assumptions, no fallbacks
            let admissionDate: Date | null = null;
            
            if (studentData.admissionDate) {
              if (typeof studentData.admissionDate === 'string') {
                admissionDate = new Date(studentData.admissionDate);
              } else if (typeof studentData.admissionDate?.toDate === 'function') {
                try { admissionDate = studentData.admissionDate.toDate(); } catch {}
              }
            }
            
            // Validate the parsed date
            if (admissionDate && isNaN(admissionDate.getTime())) {
              admissionDate = null;
            }
            
            console.log(`[GuardianFinance] Student ${child.firstName} admission info:`, {
              hasAdmissionDate: !!admissionDate,
              admissionDateRaw: studentData.admissionDate,
              admissionDate: admissionDate?.toISOString(),
              isOngoingStudent: !!studentData.ongoing,
              outstandingBalance: studentData.outstandingBalance || 0
            });
            
            // If admission date is missing, flag this student as having missing data
            // Do NOT default or guess - financial accuracy is critical
            if (!admissionDate) {
              console.warn(`[GuardianFinance] CRITICAL: No admission date found for ${child.firstName} ${child.lastName}. Cannot calculate fees accurately.`);
              
              perChildFees[child.registrationNumber] = {
                totals: { term1: 0, term2: 0, term3: 0, annual: 0 },
                type: studentType === "Day School" ? "Day School" : "Boarding",
                governmentApplied: { term1: 0, term2: 0, term3: 0, annual: 0 },
                isGovernmentSponsored: false,
                governmentSponsorshipAmount: 0,
                admissionDateMissing: true, // Flag for UI to show error
                errorMessage: "Admission date missing. Please contact school administration to update student records.",
                className: studentClass
              };
              
              // Skip fee calculation for this student
              continue;
            }
            

            
            // Find matching level fees
            let matchingLevel = null;
            
            // Try to match by class name first
            if (studentClass) {
              matchingLevel = levelFees.find((level: any) => 
                level.classes && level.classes.some((c: string) => 
                  c.toLowerCase() === studentClass.toLowerCase() ||
                  studentClass.toLowerCase().includes(c.toLowerCase())
                )
              );
            }

            // If no match by class, try by level/grade
            if (!matchingLevel && studentGrade) {
              matchingLevel = levelFees.find((level: any) => 
                level.level && level.level.toLowerCase() === studentGrade.toLowerCase()
              );
            }

            if (matchingLevel) {
              const isDaySchool = studentType === "Day School";
              const isOngoing = !!studentData.ongoing;

              // All logic (paidOnce, Government Deductions, and Term specific joining waivers)
              // is now natively handled by the standardized utility
              const applicable = getApplicableTermFees(admissionDate, matchingLevel, terms, isOngoing, isDaySchool);
              
              const t1 = applicable.term1;
              const t2 = applicable.term2;
              const t3 = applicable.term3;
              
              // We calculate annual gross here to store it for the summary card.
              const annualGross = t1 + t2 + t3;
              
              // Government sponsorship was inherently subtracted in the util. We just log the configured amount to show as 'Deduction' visually if needed
              const isGov = !!(matchingLevel.isGovernmentSponsored && matchingLevel.governmentSponsorshipAmount);
              const annualDeduction = isGov ? Number(matchingLevel.governmentSponsorshipAmount || 0) : 0;
              // === INTEGRATE OPENING BALANCE / CREDIT ===
              // Read outstandingBalance from student record
              // Positive = Debt, Negative = Credit (Surplus)
              const openingBalance = Number(studentData.outstandingBalance || 0);
              
              const studentTotal = t1 + t2 + t3 + openingBalance;

              // For display/logging: gross = net + gov deduction (approximate per-term split)
              // The util already subtracted gov sponsorship from net values
              const grossAnnual = annualGross + annualDeduction;

              console.log(`[GuardianFinance] Final Calculation for ${child.firstName}:`, {
                  netT1: t1,
                  netT2: t2,
                  netT3: t3,
                  governmentDeduction: annualDeduction,
                  openingBalance,
                  annual: studentTotal
              });

              totalRequired += studentTotal;
              
              // === NEXT CLASS TERM 1 FEE LOOKUP ===
              // When student finishes Term 3, their next fee is Term 1 of their NEXT class
              let nextClassTerm1Fee = 0;
              let nextClassName = "";
              
              try {
                // Find the current class document to get nextClassUid
                const classesRef = collection(db, `schools/${schoolID}/classes`);
                const classQuery = query(classesRef, where("name", "==", studentClass));
                const classSnap = await getDocs(classQuery);
                
                if (!classSnap.empty) {
                  const currentClassData = classSnap.docs[0].data();
                  const nextClassUid = currentClassData.nextClassUid;
                  
                  if (nextClassUid && nextClassUid !== 'ALUMNI') {
                    // Fetch the next class to get its name
                    const nextClassDoc = await getDoc(doc(db, `schools/${schoolID}/classes`, nextClassUid));
                    
                    if (nextClassDoc.exists()) {
                      nextClassName = nextClassDoc.data().name || "";
                      
                      // Find fee structure for the next class
                      const nextMatchingLevel = levelFees.find((level: any) => 
                        level.classes && level.classes.some((c: string) => 
                          c.toLowerCase() === nextClassName.toLowerCase() ||
                          nextClassName.toLowerCase().includes(c.toLowerCase())
                        )
                      );
                      
                      if (nextMatchingLevel) {
                        // Use centralized utility for next class fee calculation
                        const nextApplicable = getApplicableTermFees(undefined, nextMatchingLevel, [], true, isDaySchool);
                        nextClassTerm1Fee = nextApplicable.term1;
                        console.log(`[GuardianFinance] Next class for ${child.firstName}: ${nextClassName}, Term 1 Fee: ${nextClassTerm1Fee}`);
                      }
                    }
                  } else if (nextClassUid === 'ALUMNI') {
                    nextClassName = "Graduating";
                    console.log(`[GuardianFinance] ${child.firstName} is graduating after this year`);
                  }
                }
              } catch (nextClassError) {
                console.warn(`[GuardianFinance] Could not fetch next class for ${child.firstName}:`, nextClassError);
              }
              
              // Get termIds for this child's class from the fee structure
              const childTermIds = feeStructure.termIdsByClass?.[studentClass] || 
                                   matchingLevel.termIdsByClass?.[studentClass] ||
                                   undefined;

              perChildFees[child.registrationNumber] = {
                totals: { term1: t1, term2: t2, term3: t3, annual: studentTotal },
                grossTotals: { term1: t1, term2: t2, term3: t3, annual: grossAnnual },
                type: isDaySchool ? "Day School" : "Boarding",
                governmentApplied: { term1: 0, term2: 0, term3: 0, annual: annualDeduction },
                isGovernmentSponsored: isGov,
                governmentSponsorshipAmount: annualDeduction,
                admissionDateMissing: false,
                admissionDate: admissionDate.toISOString().split('T')[0],
                nextClassTerm1Fee,
                nextClassName,
                className: studentClass,
                termIds: childTermIds,
                openingBalance
              };
              
              console.log(`[GuardianFinance] Fees for ${child.firstName} ${child.lastName}:`, {
                class: studentClass,
                grade: studentGrade,
                type: studentType || 'Boarding',
                admissionDate: admissionDate.toISOString(),
                netT1: t1, netT2: t2, netT3: t3,
                governmentDeduction: annualDeduction,
                total: studentTotal
              });
            } else {
              console.log(`No fee structure found for ${child.firstName} ${child.lastName} (class: ${studentClass}, grade: ${studentGrade})`);
            }
          }
        }

        setTotalFeesRequired(totalRequired);
        setChildFeesRequiredMap(perChildFees);
      } catch (error) {
        console.error("Error fetching total fees required:", error);
        toast({
          title: "Error",
          description: "Failed to calculate total fees required",
          variant: "destructive",
          duration: 3000,
        });
        setTotalFeesRequired(0);
      } finally {
        setLoadingFeesRequired(false);
      }
    };

    fetchTotalFeesRequired();
  }, [schoolID, children, toast, termRanges, childPaymentsByTerm]);

  // Calculate balance with context awareness (Current Term Only)
  useEffect(() => {
    if (Object.keys(childFeesRequiredMap).length === 0) {
      if (balance !== 0) setBalance(0);
      return;
    }

    let calculatedOutstanding = 0;
    const currentTermKey = getTermKeyFromDate(new Date());

    Object.keys(childFeesRequiredMap).forEach(regNo => {
      const per = childFeesRequiredMap[regNo];
      const paid = childPaymentsByTerm[regNo] || { term1: 0, term2: 0, term3: 0 };
      
      const { totalOutstanding } = calculateFeeBalances(
        per.totals, 
        paid, 
        per.openingBalance, 
        currentTermKey
      );
      
      calculatedOutstanding += totalOutstanding;
    });

    setBalance(calculatedOutstanding);
  }, [childFeesRequiredMap, childPaymentsByTerm]);

  // Removed transaction fetch logic (handled in GuardianTransactions)

  const normalizePhone = (p: string) => {
    if (!p) return p;
    let v = p.trim();
    if (v.startsWith("+")) v = v.slice(1);
    if (v.startsWith("0")) v = `254${v.slice(1)}`;
    if (/^\d{9}$/.test(v)) v = `254${v}`;
    return v;
  };

  // Listen to pending transaction status
  useEffect(() => {
    if (!schoolID || !pendingTransactionId) {
      if (!schoolID) console.log("[GuardianFinance] No schoolID yet; skip tx listener");
      if (!pendingTransactionId) console.log("[GuardianFinance] No pendingTransactionId; skip tx listener");
      return;
    }

    console.log("[GuardianFinance] Attaching onSnapshot to:", `schools/${schoolID}/transactions/mpesa/records/${pendingTransactionId}`);
    addDebugLog("🔗 Attaching listener", { 
      path: `schools/${schoolID}/transactions/mpesa/records/${pendingTransactionId}`,
      schoolID,
      checkoutRequestID: pendingTransactionId
    });
    
    const txDocRef = doc(db, `schools/${schoolID}/transactions/mpesa/records/${pendingTransactionId}`);
    
    // Set timeout for pending payment (2 minutes)
    const timeoutId = setTimeout(() => {
      if (paymentStatus === "processing") {
        console.warn("[GuardianFinance] Payment timeout reached (120s)");
        toast({
          title: "⏱️ Payment Taking Longer Than Expected",
          description: "Your payment is still being processed. Please check transaction history in a few moments.",
          duration: 10000,
        });
      }
    }, 120000);
    
    // Background auto-check: Automatically call handleCheckStatus every 5 seconds, 10 times
    let autoCheckCount = 0;
    const maxAutoChecks = 10;
    const autoCheckInterval = setInterval(() => {
      autoCheckCount++;
      console.log(`[GuardianFinance] Auto-check ${autoCheckCount}/${maxAutoChecks}`);
      addDebugLog(`🔄 Auto-check ${autoCheckCount}/${maxAutoChecks}`, { checkoutRequestID: pendingTransactionId });
      
      // Call the same handleCheckStatus function
      handleCheckStatus();
      
      if (autoCheckCount >= maxAutoChecks) {
        clearInterval(autoCheckInterval);
        console.log("[GuardianFinance] Completed all auto-checks");
      }
    }, 5000); // Every 5 seconds
    
    const unsubscribe = onSnapshot(txDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        console.log("[GuardianFinance] onSnapshot update:", { status: data.status, receipt: data?.mpesaDetails?.receiptNumber, amount: data?.amount, resultCode: data?.resultCode, resultDesc: data?.resultDesc });
        addDebugLog("📡 Firestore update received", {
          status: data.status,
          amount: data.amount,
          receipt: data?.mpesaDetails?.receiptNumber,
          resultCode: data?.resultCode,
          resultDesc: data?.resultDesc,
          lastUpdate: data?.lastUpdate,
          fullDocument: data
        });
        
        if (data.status === "completed") {
          setPaymentStatus("completed");
          clearTimeout(timeoutId);
          clearInterval(autoCheckInterval); // Stop auto-checking when completed
          
          // Clear form immediately
          setAmount("");
          setPhoneNumber("");
          setPendingTransactionId(null);
          setPayResult(null);
          
          // Show centered success modal
          setSuccessModalData({
            amount: data.amount,
            receipt: data.mpesaDetails?.receiptNumber,
            message: "Your payment has been received successfully!"
          });
          setShowSuccessModal(true);
        } else if (data.status === "failed") {
          setPaymentStatus("failed");
          clearTimeout(timeoutId);
          clearInterval(autoCheckInterval); // Stop auto-checking when failed
          toast({
            title: "❌ Payment Failed",
            description: data.resultDesc || data.errorMessage || "The payment could not be completed. Please try again.",
            variant: "destructive",
            duration: 8000,
          });
          setPendingTransactionId(null);
          setPayResult(null);
        } else if (data.status === "pending") {
          setPaymentStatus("processing");
        }
      } else {
        console.log("[GuardianFinance] onSnapshot no doc yet for", pendingTransactionId);
        addDebugLog("⚠️ Document not found yet", { checkoutRequestID: pendingTransactionId });
      }
    }, (error) => {
      console.error("[GuardianFinance] Transaction listener error:", error);
      addDebugLog("❌ Listener error", { error: error.message, code: error.code });
      clearTimeout(timeoutId);
      clearInterval(autoCheckInterval);
    });

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
      clearInterval(autoCheckInterval);
    };
  }, [schoolID, pendingTransactionId, toast, paymentStatus]);

  const handlePay = async () => {
    setPayError(null);
    setPayLoading(true);
    try {
      if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
        throw new Error("Please enter a valid amount");
      }
      if (!phoneNumber) throw new Error("Please provide a phone number");

      const normalized = normalizePhone(phoneNumber);
      const guardianInitiate = httpsCallable(functions, "guardianInitiateStkPush");

      // Get the child's fee info to extract termIds
      const childRegNumber = children[0]?.registrationNumber;
      const childFeeInfo = childRegNumber ? childFeesRequiredMap[childRegNumber] : null;
      const childTermIds = childFeeInfo?.termIds;
      
      // Determine the actual termId to send with the payment
      const currentTermId = childTermIds ? childTermIds[selectedTerm] : null;
      const termNumber = selectedTerm === 'term1' ? 1 : selectedTerm === 'term2' ? 2 : 3;
      const currentYear = new Date().getFullYear();
      const termKey = `${selectedTerm}_${currentYear}`;

      console.log("[GuardianFinance] Initiating STK Push with:", { 
        schoolId: schoolID, 
        amount: Number(amount), 
        phoneNumber: normalized, 
        accountReference: accountReference || `SchoolFee-${childRegNumber || "UNKNOWN"}`,
        termId: currentTermId,
        termKey,
        paymentForTerm: termNumber
      });
      addDebugLog("🚀 Initiating STK Push", {
        schoolId: schoolID,
        amount: Number(amount),
        phoneNumber: normalized,
        accountReference: accountReference || `SchoolFee-${childRegNumber || "UNKNOWN"}`,
        termId: currentTermId,
        termKey,
        paymentForTerm: termNumber
      });
      
      const resp = await guardianInitiate({
        schoolId: schoolID,
        amount: Number(amount),
        phoneNumber: normalized,
        accountReference: accountReference || `SchoolFee-${childRegNumber || "UNKNOWN"}`,
        termId: currentTermId, // Term ID from fee structure (e.g., 1, 2, 3 for Form 1)
        termKey, // e.g., "term1_2026"
        paymentForTerm: termNumber, // 1, 2, or 3
      });

      console.log("[GuardianFinance] guardianInitiateStkPush raw resp:", resp);
      const responseData = resp.data as any;
      setPayResult(responseData);
      console.log("[GuardianFinance] Parsed response data:", responseData);
      addDebugLog("✅ STK Push response received", {
        success: responseData?.success,
        merchantRequestID: responseData?.data?.MerchantRequestID,
        checkoutRequestID: responseData?.data?.CheckoutRequestID,
        responseCode: responseData?.data?.ResponseCode,
        responseDescription: responseData?.data?.ResponseDescription,
        fullResponse: responseData
      });

      // Extract CheckoutRequestID for real-time listener
      const checkoutId = responseData?.data?.CheckoutRequestID || responseData?.CheckoutRequestID;
      console.log("[GuardianFinance] Extracted CheckoutRequestID:", checkoutId);
      addDebugLog("🔑 CheckoutRequestID extracted", { checkoutRequestID: checkoutId });
      
      if (checkoutId) {
        setPendingTransactionId(checkoutId);
        setPaymentStatus("processing");
      } else {
        console.warn("[GuardianFinance] STK Push response missing CheckoutRequestID; cannot attach listener for completion.");
        addDebugLog("⚠️ Missing CheckoutRequestID", { response: responseData });
      }
      
      toast({ 
        title: "📱 Payment Request Sent", 
        description: "Check your phone to enter your M-Pesa PIN and complete the payment.", 
        duration: 10000 
      });
    } catch (err: any) {
      setPayError(err.message || String(err));
      setPaymentStatus("idle");
      addDebugLog("❌ Payment initiation failed", { error: err.message, code: err.code, stack: err.stack });
      toast({ 
        title: "Payment Initiation Failed", 
        description: err.message || String(err), 
        variant: "destructive",
        duration: 7000
      });
    } finally {
      setPayLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!pendingTransactionId || !schoolID) return;
    
    setCheckingStatus(true);
    addDebugLog("🔍 Manual status check initiated", { checkoutRequestID: pendingTransactionId, schoolID });
    
    try {
      // Query M-Pesa directly for transaction status
      const queryMpesaStatus = httpsCallable(functions, "queryMpesaStkStatus");
      const result = await queryMpesaStatus({
        schoolId: schoolID,
        checkoutRequestId: pendingTransactionId,
      });
      
      const response = result.data as any;
      addDebugLog("✅ M-Pesa query response", response);
      
      console.log("[GuardianFinance] M-Pesa query result:", response);
      
      // Check Firestore after query (it may have been updated)
      const txDocRef = doc(db, `schools/${schoolID}/transactions/mpesa/records/${pendingTransactionId}`);
      const snap = await import("firebase/firestore").then(m => m.getDoc(txDocRef));
      
      if (snap.exists()) {
        const data = snap.data();
        addDebugLog("📄 Firestore transaction after query", data);
        
        // Show prominent success message if payment completed
        if (data.status === "completed") {
          setPaymentStatus("completed");
          setPendingTransactionId(null);
          
          // Clear the payment form
          setAmount("");
          setPhoneNumber("");
          setPayResult(null);
          
          // Show centered success modal
          setSuccessModalData({
            amount: data.amount,
            receipt: data.mpesaDetails?.receiptNumber,
            message: "Your payment has been confirmed successfully!"
          });
          setShowSuccessModal(true);
        } else if (data.status === "failed") {
          setPaymentStatus("failed");
          setPendingTransactionId(null);
          
          toast({
            title: "❌ Payment Failed",
            description: response.resultDesc || data.resultDesc || "The payment could not be completed.",
            variant: "destructive",
            duration: 8000,
          });
        } else {
          // Still pending
          toast({
            title: "📊 Payment Status: PENDING",
            description: `${response.resultDesc || 'Payment is still being processed'} | Amount: KES ${data.amount?.toLocaleString()}`,
            duration: 8000,
          });
        }
      } else {
        toast({
          title: response.resultCode === "0" ? "⚠️ Payment Successful (Pending Update)" : "📊 Status Retrieved",
          description: response.resultDesc || "Status queried from M-Pesa",
          duration: 8000,
        });
      }
    } catch (err: any) {
      console.error("[GuardianFinance] Status check error:", err);
      addDebugLog("❌ Status check error", { error: err.message, code: err.code });
      
      toast({
        title: "Error Checking Status",
        description: err.message || "Failed to retrieve payment status",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  // Filtering now handled in dedicated transactions page

  // formatDate helper removed (unused after transactions extracted)

  // Status color function moved to dedicated page

  return (
    <>
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <SidebarProvider className="h-full overflow-hidden">
          <div className="flex h-full w-full">
            <GuardianSidebar />
            <div className="flex flex-col flex-1 h-full w-full min-w-0">
              <GuardianHeader />
              <main className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-6">
            <div className="max-w-6xl mx-auto min-h-full">
              {isLoading ? (
                <div className="flex items-center justify-center h-[60vh]">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-10 w-10 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Verifying guardian access...</p>
                  </div>
                </div>
              ) : !hasAccess ? (
                <div className="flex items-center justify-center h-[60vh]">
                  <p className="text-red-500">Access Denied</p>
                </div>
              ) : !schoolID ? (
                <div className="max-w-2xl mx-auto mt-20">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                    <div className="mb-6">
                      <svg
                        className="mx-auto h-16 w-16 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      Not Linked to a School Yet
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                      Your guardian account is not currently linked to any school. Please contact your school's administrator to get invited and linked to your child's school.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        💡 Once the school administrator sends you an invitation and you accept it, you'll be able to access finance information and make payments.
                      </p>
                    </div>
                  </div>
                </div>
              ) : !showTransactions ? (
                <>
                  <div className="mb-4 sm:mb-6">
                    <div className="flex justify-center mb-6">
                      <nav className="flex space-x-2 bg-gray-200 dark:bg-gray-800 p-1 rounded-lg">
                        <button
                          onClick={() => setActiveTab("overview")}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            activeTab === "overview"
                              ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          Overview
                        </button>
                        <button
                          onClick={() => setActiveTab("payments")}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            activeTab === "payments"
                              ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          Payments
                        </button>
                        <button
                          onClick={() => setActiveTab("feeStructure")}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            activeTab === "feeStructure"
                              ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          Fee Structure
                        </button>
                      </nav>
                    </div>
                    {activeTab === "overview" ? (
                      <>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                          Financial Overview
                        </h1>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                          Summary of total fees paid and payment history
                        </p>
                      </>
                    ) : activeTab === "payments" ? (
                      <>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                          Finance & Payments
                        </h1>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                          View fees, make payments, and track your child’s financial records
                        </p>
                      </>
                    ) : (
                      <>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                          School Fee Structure
                        </h1>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                          Review the current academic year fee breakdown by level and term
                        </p>
                      </>
                    )}
                  </div>

                  {activeTab === "overview" && (
                    <div className="space-y-6">
                      {/* Termly Fee Breakdown per Child */}
                      {children.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 m-2">
                          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                              <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Termly Fee Breakdown</h2>
                                {termRanges && termRanges[selectedTerm] && (() => {
                                  const now = new Date();
                                  const termStart = termRanges[selectedTerm].start;
                                  const termEnd = termRanges[selectedTerm].end;
                                  const termLabel = selectedTerm === 'term1' ? 'Term 1' : selectedTerm === 'term2' ? 'Term 2' : 'Term 3';
                                  
                                  // Determine term status
                                  const isCurrentlyInTerm = now >= termStart && now <= termEnd;
                                  const isUpcoming = now < termStart;
                                  const hasEnded = now > termEnd;

                                  if (isCurrentlyInTerm) {
                                    return (
                                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                        <span className="font-semibold">{termLabel}</span> is currently in session (ends {termEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })})
                                      </p>
                                    );
                                  } else if (isUpcoming) {
                                    return (
                                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                        <span className="font-semibold">{termLabel}</span> begins on <span className="font-bold">{termStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                      </p>
                                    );
                                  } else if (hasEnded) {
                                    return (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        <span className="font-semibold">{termLabel}</span> ended on {termEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                      </p>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => setShowLess(!showLess)}
                                  className="h-9 text-xs font-medium border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                                >
                                  {showLess ? "Show Full Details" : "Show Less Info"}
                                </Button>
                                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                {(['term1', 'term2', 'term3'] as const).map((t) => (
                                  <button
                                    key={t}
                                    onClick={() => setSelectedTerm(t)}
                                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                                      selectedTerm === t
                                        ? 'bg-white dark:bg-gray-600 shadow text-gray-900 dark:text-white'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                                  >
                                    {t === 'term1' ? 'Term 1' : t === 'term2' ? 'Term 2' : 'Term 3'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            {children.map((child) => {
                              const per = childFeesRequiredMap[child.registrationNumber];
                              
                              if (!per) {
                                return (
                                  <div key={child.studentUid} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 m-2 flex items-center justify-center min-h-[200px]">
                                    <div className="flex flex-col items-center gap-2">
                                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                      <p className="text-sm text-muted-foreground">Loading fee details...</p>
                                    </div>
                                  </div>
                                );
                              }

                              // Check if admission date is missing - show error instead of fee data
                              if (per?.admissionDateMissing) {
                                return (
                                  <div key={child.studentUid} className="border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg p-4 m-2">
                                    <div className="flex items-start gap-3">
                                      <div className="flex-shrink-0">
                                        <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                      </div>
                                      <div className="flex-1">
                                        <h3 className="text-base font-semibold text-red-800 dark:text-red-200">
                                          {child.firstName} {child.lastName}
                                        </h3>
                                        <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                                          Reg No: <span className="font-mono">{child.registrationNumber}</span>
                                        </p>
                                        <div className="bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-md p-3">
                                          <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                            ⚠️ Cannot Calculate Fees
                                          </p>
                                          <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                            {per.errorMessage || "Admission date is missing. Please contact school administration to update student records."}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              
                              const paid = childPayments[child.registrationNumber] || 0;
                              const annual = per?.totals.annual || 0;
                              const fmt = (n: number) => {
                                const val = Math.round(n);
                                if (val < 0) return `(CR) KES ${Math.abs(val).toLocaleString()}`;
                                return `KES ${val.toLocaleString()}`;
                              };
                              const perTermPaid = childPaymentsByTerm[child.registrationNumber] || { term1: 0, term2: 0, term3: 0 };
                              const currentTermKey = getTermKeyFromDate(new Date());
                              
                              // Use shared utility for consistent calculation across the app
                              // This handles the "Carryover" logic requested: Term 2 Due = Term 2 Fee + Term 1 Balance
                              const feeStatus = calculateFeeBalances(
                                {
                                  term1: per?.totals.term1 || 0,
                                  term2: per?.totals.term2 || 0,
                                  term3: per?.totals.term3 || 0,
                                  annual: per?.totals.annual || 0
                                },
                                {
                                  term1: perTermPaid.term1 || 0,
                                  term2: perTermPaid.term2 || 0,
                                  term3: perTermPaid.term3 || 0
                                },
                                per?.openingBalance || childOutstandingBalances[child.registrationNumber] || 0, // Pass historical debt or credit
                                currentTermKey // Context-aware: only debts up to current term
                              );
                              // childBalance now derived from feeStatus (respects current term)
                              const childBalance = feeStatus.totalOutstanding;

                              // Determine upcoming term
                              // Determine upcoming term based on selectedTerm
                              const nextTermKey = selectedTerm === 'term1' ? 'term2' : selectedTerm === 'term2' ? 'term3' : 'term1';
                              
                              // When next term is Term 1 (new academic year), show NEXT CLASS fees
                              // Otherwise, show current class fees for Term 2 or Term 3
                              let upcomingRaw = 0;
                              let nextTermLabel = "";
                              
                              if (nextTermKey === 'term1') {
                                // Moving to new academic year - show next class Term 1 fees
                                if (per?.nextClassName === 'Graduating') {
                                  nextTermLabel = "Graduation Year";
                                  upcomingRaw = 0;
                                } else if (per?.nextClassTerm1Fee && per.nextClassTerm1Fee > 0) {
                                  nextTermLabel = `Term 1 (${per.nextClassName || 'Next Class'})`;
                                  upcomingRaw = per.nextClassTerm1Fee;
                                } else {
                                  nextTermLabel = "Term 1 (Next Year)";
                                  upcomingRaw = per?.totals?.term1 || 0; // Fallback to current class Term 1
                                }
                              } else {
                                // Same academic year - show current class fees
                                nextTermLabel = nextTermKey === 'term2' ? 'Term 2' : 'Term 3';
                                upcomingRaw = per?.totals ? per.totals[nextTermKey] : 0;
                              }
                              
                              // Apply credit balance to upcoming fee
                              // If childBalance is negative (credit/overpayment), it reduces the upcoming fee
                              // If childBalance is positive (still owes), upcoming stays as-is (they need to clear current first)
                              const creditToApply = childBalance < 0 ? Math.abs(childBalance) : 0;
                              const upcoming = Math.max(0, upcomingRaw - creditToApply);

                              return (
                                <div key={child.studentUid} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 m-2">
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="font-medium text-gray-900 dark:text-white">
                                        {child.firstName} {child.lastName}
                                        {per?.className && (
                                          <span className="ml-2 text-sm text-gray-500 font-normal">({per.className})</span>
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">Reg No: <span className="font-mono">{child.registrationNumber}</span> • {per?.type || '—'}</p>
                                      {per?.admissionDate && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500">Admitted: {per.admissionDate}</p>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="mb-1 h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                          onClick={() => setSimState({ 
                                              isOpen: true, 
                                              studentId: child.studentUid, 
                                              studentName: `${child.firstName} ${child.lastName}`,
                                              feeTotals: per?.totals,
                                              openingBalance: per?.openingBalance 
                                          })}
                                      >
                                          <PlayCircle className="h-3 w-3 mr-1"/> Simulate Payment
                                      </Button>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">Annual (Net)</p>
                                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{fmt(annual)}</p>
                                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Gov't Deductible: <span className="font-medium">{fmt((per?.governmentApplied?.annual || 0))}</span></p>
                                    </div>
                                  </div>
                                  {/* Opening Balance / Credit Row */}
                                  {(per?.openingBalance || 0) !== 0 && (
                                      <div className={`mx-2 mb-2 p-3 rounded-md flex justify-between items-center border ${ (per?.openingBalance || 0) > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200' }`}>
                                          <div className="flex flex-col">
                                              <span className={`text-xs font-bold uppercase ${ (per?.openingBalance || 0) > 0 ? 'text-amber-800' : 'text-green-800' }`}>
                                                  {(per?.openingBalance || 0) > 0 ? 'Unpaid Fees from Previous Term' : 'Credit Brought Forward'}
                                              </span>
                                              <span className="text-[10px] text-gray-500">From previous academic years</span>
                                          </div>
                                          <span className={`font-mono font-bold text-sm ${ (per?.openingBalance || 0) > 0 ? 'text-amber-700' : 'text-green-700' }`}>
                                              {(per?.openingBalance || 0) > 0 ? '-' : '+'}{Math.abs(per?.openingBalance || 0).toLocaleString()}
                                          </span>
                                      </div>
                                  )}

                                  {showLess ? (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-800 m-2">
                                      <div className="flex justify-between items-center mb-3">
                                         <div>
                                             <p className="text-sm font-bold text-gray-900 dark:text-white uppercase">
                                                 Current Term: {currentTermKey === 'term1' ? 'Term 1' : currentTermKey === 'term2' ? 'Term 2' : 'Term 3'}
                                             </p>
                                             <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${feeStatus[currentTermKey].status.color}`}>
                                                {feeStatus[currentTermKey].status.label}
                                             </span>
                                         </div>
                                         <div className="text-right">
                                             <p className="text-xs text-gray-500">Net Due</p>
                                             <p className="text-xl font-bold text-gray-900 dark:text-white">
                                                 {fmt(feeStatus[currentTermKey].totalDue)}
                                             </p>
                                         </div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 text-xs bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                                          <div>
                                             <p className="text-gray-500">Required</p>
                                             <p className="font-medium">{fmt(feeStatus[currentTermKey].required)}</p>
                                          </div>
                                          <div>
                                             <p className="text-gray-500">Balance from Previous Term</p>
                                             <p className="font-medium">{fmt(feeStatus[currentTermKey].carryover)}</p>
                                          </div>
                                          <div>
                                             <p className="text-gray-500">Paid</p>
                                             <p className="font-medium text-green-600">{fmt(feeStatus[currentTermKey].paid)}</p>
                                          </div>
                                      </div>
                                       <div className="mt-3 flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-2">
                                           <span className="text-sm font-medium">Outstanding Balance</span>
                                           <span className={`text-lg font-bold ${feeStatus[currentTermKey].balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                               {fmt(feeStatus[currentTermKey].balance)}
                                           </span>
                                       </div>
                                       {feeStatus[currentTermKey].balance < 0 && (
                                           <div className="mt-2 text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200">
                                               Credits/Overpayment: {fmt(Math.abs(feeStatus[currentTermKey].balance))}
                                           </div>
                                       )}
                                    </div>
                                  ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {/* Term 1 Card */}
                                    <div className={`rounded-md p-3 m-2 transition-all duration-200 ${selectedTerm === 'term1' ? 'bg-white dark:bg-gray-800 ring-2 ring-green-500 shadow-md scale-[1.02] z-10' : 'bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-700 opacity-80'}`}>
                                      <div className="flex justify-between items-center mb-3">
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Term 1</p>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${feeStatus.term1.status.color}`}>{feeStatus.term1.status.label}</span>
                                      </div>

                                      <div className="flex flex-col mb-3">
                                         {(per.grossTotals?.term1 || 0) > feeStatus.term1.required ? (
                                             <div className="text-xs space-y-1 mb-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-600">
                                                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                                                  <span>Gross Fee</span>
                                                  <span>{fmt(per.grossTotals?.term1 || 0)}</span>
                                                </div>
                                                <div className="flex justify-between text-green-600 dark:text-green-400">
                                                  <span>Less: Gov't Capitation</span>
                                                  <span>- {fmt(per?.governmentApplied?.term1 || 0)}</span>
                                                </div>
                                                <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                                                <div className="flex justify-between font-bold text-gray-900 dark:text-white">
                                                  <span>Net Payable</span>
                                                  <span>{fmt(feeStatus.term1.required)}</span>
                                                </div>
                                             </div>
                                         ) : (
                                             <div className="text-center mb-2">
                                                 <p className="text-xl font-bold text-gray-900 dark:text-white">{fmt(feeStatus.term1.required)}</p>
                                                 <p className="text-[10px] text-gray-500 uppercase font-medium">Term Fee</p>
                                             </div>
                                         )}

                                         {feeStatus.term1.carryover !== 0 && (
                                           <div className="mt-2 text-xs">
                                              <div className="flex justify-between items-center text-gray-500">
                                                <span>{feeStatus.term1.carryover > 0 ? 'Unpaid Fees from Prev Term' : 'Credit from Prev Term'}</span>
                                                <span className={`${feeStatus.term1.carryover > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                    {feeStatus.term1.carryover > 0 ? '+' : ''}{fmt(feeStatus.term1.carryover)}
                                                </span>
                                              </div>
                                              <div className="flex justify-between items-center mt-1 font-bold border-t border-gray-200 dark:border-gray-600 pt-1">
                                                <span className="text-gray-700 dark:text-gray-300">Total Due</span>
                                                <span className="text-gray-900 dark:text-white">{fmt(feeStatus.term1.totalDue)}</span>
                                              </div>
                                           </div>
                                         )}
                                      </div>

                                      <div className="space-y-1 bg-white dark:bg-gray-800 rounded p-2 text-xs border border-gray-100 dark:border-gray-600">
                                          <div className="flex justify-between">
                                              <span className="text-gray-500">Paid to Date</span>
                                              <span className="font-medium text-gray-900 dark:text-white">{fmt(feeStatus.term1.paid)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                              <span className="font-medium text-gray-700 dark:text-gray-300">Balance</span>
                                              <span className={`font-bold ${feeStatus.term1.balance > 0 ? 'text-red-500' : 'text-green-500'}`}>{fmt(feeStatus.term1.balance)}</span>
                                          </div>
                                      </div>
                                    </div>
                                    {/* Term 2 Card */}
                                    <div className={`rounded-md p-3 m-2 transition-all duration-200 ${selectedTerm === 'term2' ? 'bg-white dark:bg-gray-800 ring-2 ring-green-500 shadow-md scale-[1.02] z-10' : 'bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-700 opacity-80'}`}>
                                      <div className="flex justify-between items-center mb-3">
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Term 2</p>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${feeStatus.term2.status.color}`}>{feeStatus.term2.status.label}</span>
                                      </div>

                                      <div className="flex flex-col mb-3">
                                         {(per.grossTotals?.term2 || 0) > feeStatus.term2.required ? (
                                             <div className="text-xs space-y-1 mb-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-600">
                                                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                                                  <span>Gross Fee</span>
                                                  <span>{fmt(per.grossTotals?.term2 || 0)}</span>
                                                </div>
                                                <div className="flex justify-between text-green-600 dark:text-green-400">
                                                  <span>Less: Gov't Capitation</span>
                                                  <span>- {fmt(per?.governmentApplied?.term2 || 0)}</span>
                                                </div>
                                                <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                                                <div className="flex justify-between font-bold text-gray-900 dark:text-white">
                                                  <span>Net Payable</span>
                                                  <span>{fmt(feeStatus.term2.required)}</span>
                                                </div>
                                             </div>
                                         ) : (
                                             <div className="text-center mb-2">
                                                 <p className="text-xl font-bold text-gray-900 dark:text-white">{fmt(feeStatus.term2.required)}</p>
                                                 <p className="text-[10px] text-gray-500 uppercase font-medium">Term Fee</p>
                                             </div>
                                         )}

                                         {feeStatus.term2.carryover !== 0 && (
                                           <div className="mt-2 text-xs">
                                              <div className="flex justify-between items-center text-gray-500">
                                                <span>{feeStatus.term2.carryover > 0 ? 'Unpaid Fees from Prev Term' : 'Credit from Prev Term'}</span>
                                                <span className={`${feeStatus.term2.carryover > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                    {feeStatus.term2.carryover > 0 ? '+' : ''}{fmt(feeStatus.term2.carryover)}
                                                </span>
                                              </div>
                                              <div className="flex justify-between items-center mt-1 font-bold border-t border-gray-200 dark:border-gray-600 pt-1">
                                                <span className="text-gray-700 dark:text-gray-300">Total Due</span>
                                                <span className="text-gray-900 dark:text-white">{fmt(feeStatus.term2.totalDue)}</span>
                                              </div>
                                           </div>
                                         )}
                                      </div>

                                      <div className="space-y-1 bg-white dark:bg-gray-800 rounded p-2 text-xs border border-gray-100 dark:border-gray-600">
                                          <div className="flex justify-between">
                                              <span className="text-gray-500">Paid to Date</span>
                                              <span className="font-medium text-gray-900 dark:text-white">{fmt(feeStatus.term2.paid)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                              <span className="font-medium text-gray-700 dark:text-gray-300">Balance</span>
                                              <span className={`font-bold ${feeStatus.term2.balance > 0 ? 'text-red-500' : 'text-green-500'}`}>{fmt(feeStatus.term2.balance)}</span>
                                          </div>
                                      </div>
                                    </div>
                                    {/* Term 3 Card */}
                                    <div className={`rounded-md p-3 m-2 transition-all duration-200 ${selectedTerm === 'term3' ? 'bg-white dark:bg-gray-800 ring-2 ring-green-500 shadow-md scale-[1.02] z-10' : 'bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-700 opacity-80'}`}>
                                      <div className="flex justify-between items-center mb-3">
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Term 3</p>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${feeStatus.term3.status.color}`}>{feeStatus.term3.status.label}</span>
                                      </div>

                                      <div className="flex flex-col mb-3">
                                         {(per.grossTotals?.term3 || 0) > feeStatus.term3.required ? (
                                             <div className="text-xs space-y-1 mb-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-600">
                                                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                                                  <span>Gross Fee</span>
                                                  <span>{fmt(per.grossTotals?.term3 || 0)}</span>
                                                </div>
                                                <div className="flex justify-between text-green-600 dark:text-green-400">
                                                  <span>Less: Gov't Capitation</span>
                                                  <span>- {fmt(per?.governmentApplied?.term3 || 0)}</span>
                                                </div>
                                                <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                                                <div className="flex justify-between font-bold text-gray-900 dark:text-white">
                                                  <span>Net Payable</span>
                                                  <span>{fmt(feeStatus.term3.required)}</span>
                                                </div>
                                             </div>
                                         ) : (
                                             <div className="text-center mb-2">
                                                 <p className="text-xl font-bold text-gray-900 dark:text-white">{fmt(feeStatus.term3.required)}</p>
                                                 <p className="text-[10px] text-gray-500 uppercase font-medium">Term Fee</p>
                                             </div>
                                         )}

                                         {feeStatus.term3.carryover !== 0 && (
                                           <div className="mt-2 text-xs">
                                              <div className="flex justify-between items-center text-gray-500">
                                                <span>{feeStatus.term3.carryover > 0 ? 'Unpaid Fees from Prev Term' : 'Credit from Prev Term'}</span>
                                                <span className={`${feeStatus.term3.carryover > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                    {feeStatus.term3.carryover > 0 ? '+' : ''}{fmt(feeStatus.term3.carryover)}
                                                </span>
                                              </div>
                                              <div className="flex justify-between items-center mt-1 font-bold border-t border-gray-200 dark:border-gray-600 pt-1">
                                                <span className="text-gray-700 dark:text-gray-300">Total Due</span>
                                                <span className="text-gray-900 dark:text-white">{fmt(feeStatus.term3.totalDue)}</span>
                                              </div>
                                           </div>
                                         )}
                                      </div>

                                      <div className="space-y-1 bg-white dark:bg-gray-800 rounded p-2 text-xs border border-gray-100 dark:border-gray-600">
                                          <div className="flex justify-between">
                                              <span className="text-gray-500">Paid to Date</span>
                                              <span className="font-medium text-gray-900 dark:text-white">{fmt(feeStatus.term3.paid)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                              <span className="font-medium text-gray-700 dark:text-gray-300">Balance</span>
                                              <span className={`font-bold ${feeStatus.term3.balance > 0 ? 'text-red-500' : 'text-green-500'}`}>{fmt(feeStatus.term3.balance)}</span>
                                          </div>
                                      </div>
                                    </div>
                                  </div>
                                  )}
                                  <div className="mt-3 flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300 m-2">Paid: {fmt(paid)}</span>
                                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium m-2 ${childBalance > 0 ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'}`}>
                                      {childBalance > 0 ? `Balance: ${fmt(childBalance)}` : childBalance < 0 ? `Credit: ${fmt(childBalance)}` : 'Cleared'}
                                    </span>
                                  </div>
                                  {childBalance <= 0 && (
                                    <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 rounded-md p-3 m-2">
                                      <p className="text-sm font-medium">Upcoming Fee</p>
                                      
                                      {/* Due Date & Advice */}
                                      <div className="mb-2 text-xs text-blue-700 dark:text-blue-300">
                                         {termRanges && termRanges[nextTermKey] ? (() => {
                                             const d = new Date(termRanges[nextTermKey].start);
                                             // If upcoming is Term 1 (Next Year), add 1 year to the date
                                             if (nextTermKey === 'term1') d.setFullYear(d.getFullYear() + 1);
                                             return (
                                              <p className="mb-1">
                                                <span className="font-semibold">Due Date:</span> {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                              </p>
                                             );
                                          })() : null}
                                         <p className="italic opacity-90">
                                           Advice: Please pay before the due date to avoid invalidation of admission.
                                         </p>
                                      </div>

                                      {creditToApply > 0 ? (
                                        <div className="text-xs space-y-0.5 border-t border-blue-200 dark:border-blue-700 pt-2">
                                          <div className="flex justify-between opacity-75">
                                            <span>{nextTermLabel}</span>
                                            <span>{fmt(upcomingRaw)}</span>
                                          </div>
                                          <div className="flex justify-between text-green-600 dark:text-green-400">
                                            <span>Less: Credit Applied</span>
                                            <span>- {fmt(creditToApply)}</span>
                                          </div>
                                          <div className="border-t border-blue-200 dark:border-blue-700 mt-1 pt-1">
                                            <div className="flex justify-between font-semibold">
                                              <span>Net Due</span>
                                              <span>{fmt(upcoming)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-xs opacity-90 border-t border-blue-200 dark:border-blue-700 pt-2 mt-1">{nextTermLabel}: <span className="font-semibold">{fmt(upcoming || 0)}</span></p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Total Fees Paid Card */}
                      <div className="bg-gradient-to-br from-blue-500 to-blue-700 dark:from-blue-600 dark:to-blue-800 rounded-lg shadow-lg p-6 text-white relative group">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-blue-100 text-sm font-medium mb-1">Total Fees Paid</p>
                            {loadingTotalFees ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-6 w-6 animate-spin" />
                                <p className="text-2xl font-bold">Calculating...</p>
                              </div>
                            ) : (
                              <p className="text-4xl font-bold">KES {totalFeesPaid.toLocaleString()}</p>
                            )}
                          </div>
                          <div className="text-right">
                             <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm -mt-2">
                               <p className="text-xs text-blue-100 mb-1">Fee Structure</p>
                               <span className="bg-white/20 px-2 py-1 rounded text-xs font-bold">
                                  {new Date().getFullYear().toString()}
                               </span>
                             </div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-blue-400/30 flex justify-between items-center">
                          <p className="text-blue-100 text-xs">
                            Total payments made for {children.length} {children.length === 1 ? 'child' : 'children'}
                          </p>
                          <Button 
                             disabled={children.length === 0}
                             onClick={() => {
                               if (children.length > 0) {
                                 const target = children[0];
                                 const per = childFeesRequiredMap[target.registrationNumber];
                                 setSimState({
                                   isOpen: true,
                                   studentId: target.studentUid,
                                   studentName: `${target.firstName} ${target.lastName}` || 'Student',
                                   feeTotals: per?.totals,
                                   openingBalance: per?.openingBalance
                                 });
                               }
                             }}
                             className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 hover:bg-white/30 text-white text-xs h-7 px-2"
                             size="sm"
                          >
                            Simulate Payment
                          </Button>
                        </div>
                      </div>

                      {/* Total Fees Required Card */}
                      <div className="bg-gradient-to-br from-purple-500 to-purple-700 dark:from-purple-600 dark:to-purple-800 rounded-lg shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-purple-100 text-sm font-medium mb-1">Total Fees Required</p>
                            {loadingFeesRequired ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-6 w-6 animate-spin" />
                                <p className="text-2xl font-bold">Calculating...</p>
                              </div>
                            ) : (
                              <p className="text-4xl font-bold">KES {totalFeesRequired.toLocaleString()}</p>
                            )}
                          </div>
                          <div className="bg-white/20 p-4 rounded-full">
                            <DollarSign className="h-10 w-10" />
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-purple-400/30">
                          <p className="text-purple-100 text-xs">
                            Annual fees for all {children.length} {children.length === 1 ? 'child' : 'children'}
                          </p>
                        </div>
                      </div>

                      {/* Balance Card */}
                      <div className={`bg-gradient-to-br ${balance > 0 ? 'from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700' : 'from-green-500 to-green-700 dark:from-green-600 dark:to-green-800'} rounded-lg shadow-lg p-6 text-white`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`${balance > 0 ? 'text-amber-100' : 'text-green-100'} text-sm font-medium mb-1`}>
                              {balance > 0 ? 'Balance Owed' : balance < 0 ? 'Overpaid' : 'Fully Paid'}
                            </p>
                            {(loadingTotalFees || loadingFeesRequired) ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-6 w-6 animate-spin" />
                                <p className="text-2xl font-bold">Calculating...</p>
                              </div>
                            ) : (
                              <p className="text-4xl font-bold">KES {Math.abs(balance).toLocaleString()}</p>
                            )}
                          </div>
                          <div className="bg-white/20 p-4 rounded-full">
                            <DollarSign className="h-10 w-10" />
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/30">
                          <p className={`${balance > 0 ? 'text-amber-100' : 'text-green-100'} text-xs`}>
                            {balance > 0 
                              ? 'Outstanding balance to be paid' 
                              : balance < 0 
                                ? 'Amount paid above required fees'
                                : 'All fees have been fully paid'}
                          </p>
                        </div>
                      </div>

                      {/* Children Summary */}
                      {children.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Your {children.length > 1 ? 'Children' : 'Child'}
                          </h2>
                          <div className="space-y-3">
                            {children.map((child) => (
                              <div
                                key={child.studentUid}
                                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              >
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {child.firstName} {child.lastName}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Reg No: <span className="font-mono">{child.registrationNumber}</span>
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Student ID</p>
                                  <code className="text-xs font-mono bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                                    {child.studentUid.substring(0, 8)}...
                                  </code>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quick Actions / Payment Methods */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Payment Method</h2>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* M-Pesa Option (Always primary) */}
                          <div
                            onClick={() => setActiveTab("payments")}
                            className={`relative overflow-hidden cursor-pointer group rounded-xl border-2 transition-all p-5 flex flex-col items-center justify-center text-center 
                              hover:border-green-500 hover:shadow-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
                            `}
                          >
                            <div className="absolute inset-0 bg-green-50 dark:bg-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform relative z-10">
                              <Phone className="h-8 w-8 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1 relative z-10">Pay via M-Pesa</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 relative z-10">Automatic update to your fee balance.</p>
                          </div>

                          {/* Cash Option (Conditional based on Firebase config) */}
                          {paymentProviders.includes("cash") && (
                            <div
                              onClick={() => {
                                toast({
                                  title: "Physical Cash Payment",
                                  description: "Please visit the school accounts office directly to make a physical cash payment. Ensure you get a receipt.",
                                  duration: 6000,
                                });
                              }}
                              className={`relative overflow-hidden cursor-pointer group rounded-xl border-2 transition-all p-5 flex flex-col items-center justify-center text-center 
                                hover:border-emerald-500 hover:shadow-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
                              `}
                            >
                              <div className="absolute inset-0 bg-emerald-50 dark:bg-emerald-900/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div className="bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform relative z-10">
                                <Coins className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 relative z-10">Pay Cash at School</h3>
                              <p className="text-xs text-gray-500 dark:text-gray-400 relative z-10">Visit accounts office in person.</p>
                            </div>
                          )}

                          {/* Bank Option (Conditional) */}
                          {(paymentProviders.includes("equity") || paymentProviders.includes("kcb")) && (
                            <div
                              onClick={() => {
                                toast({
                                  title: "Bank Transfer",
                                  description: "Please contact the school office for the official bank account details.",
                                  duration: 5000,
                                });
                              }}
                              className={`relative overflow-hidden cursor-pointer group rounded-xl border-2 transition-all p-5 flex flex-col items-center justify-center text-center 
                                hover:border-blue-500 hover:shadow-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
                              `}
                            >
                              <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform relative z-10">
                                <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                              </div>
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-1 relative z-10">Bank Deposit</h3>
                              <p className="text-xs text-gray-500 dark:text-gray-400 relative z-10">Deposit at branch or via agent.</p>
                            </div>
                          )}

                          {/* Transactions Option */}
                          <div
                            onClick={() => setShowTransactions(true)}
                            className={`relative overflow-hidden cursor-pointer group rounded-xl border-2 transition-all p-5 flex flex-col items-center justify-center text-center 
                              hover:border-gray-500 hover:shadow-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
                            `}
                          >
                            <div className="absolute inset-0 bg-gray-50 dark:bg-gray-900/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform relative z-10">
                              <Receipt className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                            </div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1 relative z-10">View Transactions</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 relative z-10">See your payment history.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "payments" && (
                    <>
                      {children.length > 0 && (
                        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-5">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Your Child{children.length > 1 ? "ren" : ""}:
                          </p>
                          <div className="space-y-2">
                            {children.map((child) => (
                              <div
                                key={child.studentUid}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {child.firstName} {child.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Reg No: <span className="font-mono">{child.registrationNumber}</span>
                                  </p>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: <code className="font-mono bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                                    {child.studentUid}
                                  </code>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* BANK DEPOSIT SIMULATION CARD */}
                      <div className="mb-6 bg-amber-50 dark:bg-amber-950/20 rounded-lg shadow p-5 border border-amber-200 dark:border-amber-900/50">
                        <div className="flex items-start gap-4 flex-col md:flex-row">
                          <div className="bg-amber-100 p-3 rounded-full dark:bg-amber-900/50">
                            <Building2 className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="flex-1 w-full space-y-4">
                            <div>
                              <h3 className="font-bold text-lg text-amber-800 dark:text-amber-500">Simulate External Bank Drop</h3>
                              <p className="text-sm text-amber-700/80 dark:text-amber-500/80">
                                Test how the system reacts when a parent deposits money directly at a bank branch via Cheque or EFT.
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-amber-800 dark:text-amber-500 mb-1.5">Deposit Type</label>
                                <select 
                                  className="flex h-10 w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm dark:border-amber-800 dark:bg-gray-900 text-gray-900 dark:text-white"
                                  id="simMethod"
                                >
                                  <option value="cheque">Physical Cheque</option>
                                  <option value="eft">EFT / Wire Transfer</option>
                                  <option value="direct_deposit">Direct Cash Deposit</option>
                                </select>
                              </div>
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-amber-800 dark:text-amber-500 mb-1.5">Amount (KES)</label>
                                <input 
                                  id="simAmount" 
                                  type="number" 
                                  placeholder="e.g. 5000" 
                                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm border-amber-300 bg-white dark:border-amber-800 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500" 
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="block text-sm font-semibold text-amber-800 dark:text-amber-500 mb-1.5">Bank Ref / Slip No</label>
                                <input 
                                  id="simRef" 
                                  type="text" 
                                  defaultValue={children.length > 0 ? `DEPOSIT FOR ${children[0].registrationNumber}` : 'DEP-1234'} 
                                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm border-amber-300 bg-white dark:border-amber-800 dark:bg-gray-900 font-mono text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500" 
                                />
                              </div>
                              <Button 
                                disabled={payLoading}
                                onClick={async () => {
                                  const amtInput = (document.getElementById('simAmount') as HTMLInputElement)?.value;
                                  const methodInput = (document.getElementById('simMethod') as HTMLSelectElement)?.value;
                                  const refInput = (document.getElementById('simRef') as HTMLInputElement)?.value;
                                  if (!amtInput || isNaN(Number(amtInput)) || Number(amtInput) <= 0) {
                                    toast({ title: "Error", description: "Enter a valid amount", variant: "destructive" });
                                    return;
                                  }
                                  if (!schoolID || children.length === 0) return;
                                  setPayLoading(true);
                                  try {
                                     const { addDoc, collection, Timestamp, serverTimestamp } = await import('firebase/firestore');
                                     const targetChild = children[0];
                                     const txId = `SIM_BNK_${Date.now()}`;
                                     
                                     // Calculate term context just like Cash Payments/M-Pesa
                                     const termNumber = selectedTerm === 'term1' ? 1 : selectedTerm === 'term2' ? 2 : 3;
                                     const matchingTerm = terms.find(t => t.number === termNumber);
                                     
                                     const activeTermLabel = matchingTerm?.title || `Term ${termNumber} ${new Date().getFullYear()}`;
                                     const activeTermKey = matchingTerm?.id || `${selectedTerm}_${new Date().getFullYear()}`;

                                     await addDoc(collection(db, `schools/${schoolID}/transactions/bank/records`), {
                                        studentName: `${targetChild.firstName} ${targetChild.lastName}`,
                                        amount: Number(amtInput),
                                        provider: "bank",
                                        status: "completed", // Force completed to match Cash/M-Pesa behavior
                                        type: "credit",
                                        transactionDate: serverTimestamp(),
                                        createdAt: serverTimestamp(),
                                        reference: txId,
                                        accountReference: `Fee-${targetChild.registrationNumber}`,
                                        originalNarration: refInput || `Simulated ${methodInput} deposit`,
                                        allocationStatus: "allocated",
                                        depositType: methodInput,
                                        createdBy: "guardian-sim-ui",
                                        termLabel: activeTermLabel,
                                        termKey: activeTermKey,
                                        paymentForTerm: termNumber,
                                        ...(methodInput === 'cheque' && {
                                            chequeNumber: `CHQ-${Math.floor(Math.random() * 90000) + 10000}`,
                                            chequeBank: "Simulated Bank",
                                            clearanceExpectedDays: 0 // Immediate for simulation
                                         })
                                     });
                                     toast({ title: "Bank Drop Simulated", description: `Successfully emulated a ${methodInput} deposit!`, className: "bg-green-600 text-white" });
                                  } catch (err: any) {
                                    toast({ title: "Simulation Failed", description: err.message, variant: "destructive" });
                                  } finally {
                                    setPayLoading(false);
                                  }
                                }}
                                className="bg-amber-600 hover:bg-amber-700 text-white h-10 w-full flex items-center justify-center"
                              >
                                {payLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <PlayCircle className="h-4 w-4 mr-2"/>}
                                Execute Simulation
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6 mb-6">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-primary" />
                          <p className="text-lg font-medium text-gray-900 dark:text-white">Make a Payment (M-Pesa)</p>
                        </div>

                        <div className="p-5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Amount (KES)</label>
                              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="e.g. 5000" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phone Number</label>
                              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="2547XXXXXXXX" />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Account Reference</label>
                              <input type="text" value={accountReference} onChange={(e) => setAccountReference(e.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder={`FeeRef-${children[0]?.registrationNumber || 'REF'}`} />
                            </div>
                          </div>

                          <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <button onClick={handlePay} disabled={payLoading} className="inline-flex items-center justify-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 font-medium rounded-md shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-white">
                              {payLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Pay with M-Pesa"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === "feeStructure" && (
                    <div className="mt-2">
                      <FeeStructureContent schoolID={schoolID} />
                    </div>
                  )}

                  {/* View Transactions simple button (only on payments tab) */}
                  {activeTab === "payments" && (
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => setShowTransactions(true)}
                        className="inline-flex items-center px-5 py-2.5 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white text-sm font-medium rounded-md shadow transition"
                      >
                        View Transactions
                      </button>
                      
                      <button
                        onClick={() => setShowDebugPanel(!showDebugPanel)}
                        className="inline-flex items-center px-4 py-2.5 bg-orange-600 dark:bg-orange-700 hover:bg-orange-700 dark:hover:bg-orange-600 text-white text-sm font-medium rounded-md shadow transition"
                      >
                        {showDebugPanel ? "Hide" : "Show"} Debug Logs
                      </button>
                    </div>
                  )}

                  {/* Debug Panel */}
                  {showDebugPanel && activeTab === "payments" && (
                    <div className="mt-4 bg-gray-900 dark:bg-gray-950 rounded-lg shadow-lg p-4 border border-orange-500">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-500 font-bold">🐛 Debug Logs</span>
                          <span className="text-xs text-gray-400">({debugLogs.length} entries)</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const logsText = debugLogs.join('\n\n---\n\n');
                              navigator.clipboard.writeText(logsText);
                              toast({ title: "📋 Copied!", description: "Debug logs copied to clipboard", duration: 2000 });
                            }}
                            className="text-xs px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded"
                          >
                            Copy All
                          </button>
                          <button
                            onClick={() => setDebugLogs([])}
                            className="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-black rounded p-3 max-h-96 overflow-y-auto font-mono text-xs">
                        {debugLogs.length === 0 ? (
                          <p className="text-gray-500">No logs yet. Initiate a payment to see debug information.</p>
                        ) : (
                          debugLogs.map((log, index) => (
                            <div key={index} className="mb-3 pb-3 border-b border-gray-800 last:border-0">
                              <pre className="text-green-400 whitespace-pre-wrap break-words">{log}</pre>
                            </div>
                          ))
                        )}
                      </div>
                      
                      <div className="mt-3 text-xs text-gray-400">
                        <p>💡 Copy these logs and share them to debug M-Pesa callback issues</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <GuardianTransactions schoolID={schoolID} accountReference={accountReference} onBack={() => setShowTransactions(false)} />
              )}
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && successModalData && (
        <>
        {/* Desktop/tablet modal */}
        <div className="fixed inset-0 z-[9999] hidden sm:flex items-center justify-center backdrop-blur-sm bg-black/20">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                <svg
                  className="h-10 w-10 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Payment Successful!
              </h3>

              {/* Message */}
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {successModalData.message}
              </p>

              {/* Payment Details */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    KES {successModalData.amount?.toLocaleString()}
                  </span>
                </div>
                {successModalData.receipt && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Receipt:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {successModalData.receipt}
                    </span>
                  </div>
                )}
              </div>

              {/* OK Button */}
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setSuccessModalData(null);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>

        {/* Mobile success banner (fallback) */}
        <div className="fixed bottom-4 inset-x-4 z-[10001] sm:hidden">
          <div className="rounded-lg shadow-xl bg-green-600 text-white p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold">Payment Successful</p>
                <p className="text-sm opacity-90">{successModalData.message}</p>
                <div className="mt-2 text-sm opacity-90 flex items-center justify-between">
                  <span>Amount: <strong>KES {successModalData.amount?.toLocaleString()}</strong></span>
                  {successModalData.receipt && (
                    <span className="ml-2">Receipt: <code className="bg-white/20 px-1 py-0.5 rounded">{successModalData.receipt}</code></span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => { setShowSuccessModal(false); setSuccessModalData(null); }}
              className="mt-3 w-full bg-white/20 hover:bg-white/30 text-white font-medium py-2 rounded-md"
            >
              Dismiss
            </button>
          </div>
        </div>
        </>
      )}
        </SidebarProvider>
      </div>
      {/* Simulator Modal */}
      {schoolID && simState.isOpen && (
        <PaymentSimulator 
           isOpen={simState.isOpen}
           onClose={() => setSimState({ ...simState, isOpen: false })}
           studentId={simState.studentId}
           studentName={simState.studentName}
           schoolId={schoolID}
           forcedFeeTotals={simState.feeTotals}
           forcedOpeningBalance={simState.openingBalance}
        />
      )}
    </>
  );
};

export default GuardianFinance;